import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { isVehiclePhoto, resolveDocumentUrl } from "../database/documentUrl";
import { VHU_LOT_EDIT, VHU_LOT_FORM } from "../../routes/routeConstants";
import { deleteVhuLot, loadVhuLots } from "./vhuLotService";

const STATUS_OPTIONS = [
  { value: "BROUILLON", label: "Brouillon" },
  { value: "OUVERT", label: "Ouvert" },
  { value: "CLOTURE", label: "Cloture" },
  { value: "ANNULE", label: "Annule" },
];

const statusColor = {
  BROUILLON: "secondary",
  OUVERT: "success",
  CLOTURE: "dark",
  ANNULE: "danger",
};

const statusLabel = (status) => STATUS_OPTIONS.find((item) => item.value === status)?.label || status || "-";

const vehicleLabel = (vehicle) => [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`;
const vehiclePhotos = (vehicle) => ((vehicle?.photos || vehicle?.documents || []).filter(isVehiclePhoto));
const lotPhotos = (lot) => (lot.vehicules || []).flatMap(vehiclePhotos);
const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;
const dateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const hasTime = String(value).includes("T");
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(hasTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
};
const isMobileVisible = (lot) => {
  const now = new Date();
  const start = lot.dateDebut ? new Date(lot.dateDebut) : null;
  const end = lot.dateFin ? new Date(lot.dateFin) : null;
  return lot.isActif !== false && lot.status === "OUVERT" && (!start || start <= now) && (!end || end > now);
};

function VhuLotsAdminPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(location.state?.successMessage || null);
  const [photoViewer, setPhotoViewer] = useState({ open: false, photos: [], index: 0, title: "" });

  useEffect(() => {
    loadPageData();
  }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const lotResponse = await loadVhuLots();
      setLots(lotResponse?.data?.body || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const openNewLotForm = () => {
    navigate(VHU_LOT_FORM);
  };

  const openPhotoViewer = (photos, title, index = 0) => {
    if (!photos.length) return;
    setPhotoViewer({ open: true, title, photos, index });
  };

  const editLot = (lot) => {
    navigate(VHU_LOT_EDIT.replace(":id", lot.id), { state: { lot } });
  };

  const removeLot = async (lot) => {
    if (!window.confirm(`Supprimer le lot ${lot.reference || lot.titre || lot.id} ?`)) return;
    await deleteVhuLot(lot.id);
    await loadPageData();
  };

  const renderVehicleThumb = (vehicle, size = 56) => {
    const photos = vehiclePhotos(vehicle);
    const photo = photos[0];
    if (!photo) {
      return (
        <span className="vhu-thumb vhu-thumb-empty" style={{ width: size, height: size }}>
          <i className="ri-car-line fs-4 text-muted" />
        </span>
      );
    }
    return (
      <button
        type="button"
        className="vhu-thumb-button"
        onClick={(event) => {
          event.stopPropagation();
          openPhotoViewer(photos, vehicleLabel(vehicle));
        }}
      >
        <img src={resolveDocumentUrl(photo)} alt={photo?.filenameUser || photo?.filename || vehicleLabel(vehicle)} className="vhu-thumb" style={{ width: size, height: size }} />
        {photos.length > 1 && (
          <span className="vhu-photo-count">{photos.length}</span>
        )}
      </button>
    );
  };

  return (
    <div className="page-content auction-page vhu-lots-page">
      <Container fluid>
        <Breadcrumbs
          title="Lots VHU"
          breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Lots VHU", link: "#" }]}
          actions={(
            <Button color="primary" onClick={openNewLotForm}>
              <i className="ri-add-line me-1" />
              Nouveau lot
            </Button>
          )}
        />

        {successMessage && (
          <Alert color="success" className="shadow-sm border-0">
            <i className="ri-checkbox-circle-line me-1" />
            {successMessage}
          </Alert>
        )}

        <Row className="g-4">
          <Col xl={12}>
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : !lots.length ? (
              <div className="vhu-empty-state">
                <i className="ri-inbox-line" />
                <strong>Aucun lot a afficher</strong>
                <span>Creez un nouveau lot VHU pour commencer.</span>
              </div>
            ) : (
              <div className="vhu-lot-grid">
                {lots.map((lot) => {
                  const photos = lotPhotos(lot);
                  return (
                      <Card className="h-100 auction-card vhu-lot-card" key={lot.id}>
                        <CardBody>
                          <div className="vhu-card-photo-row">
                            {(lot.vehicules || []).slice(0, 4).map((vehicle) => <span key={vehicle.id}>{renderVehicleThumb(vehicle, 42)}</span>)}
                            {!photos.length && <span className="vhu-empty-photo"><i className="ri-image-line" /> Sans photo</span>}
                          </div>
                          <div className="d-flex justify-content-between gap-2 mb-2">
                            <div className="min-w-0">
                              <h6 className="mb-1 text-truncate" title={lot.titre}>{lot.titre}</h6>
                              <div className="text-muted small">{lot.reference || `Lot #${lot.id}`} - {lot.lieuStockage || "Lieu non renseigne"}</div>
                            </div>
                            <div className="d-grid gap-1 justify-items-end">
                              <Badge color={statusColor[lot.status] || "secondary"}>{statusLabel(lot.status)}</Badge>
                              {isMobileVisible(lot) && <Badge color="success" className="vhu-mobile-badge">Mobile</Badge>}
                            </div>
                          </div>
                          <Row className="g-2 mb-2 vhu-card-metrics">
                            <Col xs={6}><span className="text-muted small d-block">Vehicules</span><strong>{lot.vehicules?.length || 0}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Meilleure offre</span><strong>{moneyLabel(lot.currentBestBid)}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Fin</span><strong>{dateLabel(lot.dateFin)}</strong></Col>
                            <Col xs={6}><span className="text-muted small d-block">Offres</span><strong>{lot.bidsCount || 0}</strong></Col>
                          </Row>
                          <div className="small text-muted mb-2 text-truncate">
                            {lot.bestBidderNom ? `Meilleur acheteur: ${lot.bestBidderNom}` : "Aucune offre reçue pour le moment"}
                          </div>
                          {!!(lot.vehicules || []).length && (
                            <div className="vhu-vehicle-bid-summary mb-2">
                              {(lot.vehicules || []).slice(0, 3).map((vehicle) => (
                                <div key={vehicle.id} className="d-flex justify-content-between gap-2 small">
                                  <span className="text-truncate">{vehicleLabel(vehicle)}</span>
                                  <strong>{vehicle.bidsCount || 0} offre(s) - {moneyLabel(vehicle.currentBestBid || lot.prixDepart)}</strong>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="d-flex flex-wrap gap-2">
                            <Button size="sm" color="light" onClick={() => editLot(lot)}>Modifier</Button>
                            <Button size="sm" color="light" disabled={!photos.length} onClick={() => openPhotoViewer(photos, lot.titre || "Photos du lot")}>Voir photos</Button>
                            <Button size="sm" color="light" className="text-danger" onClick={() => removeLot(lot)}>Supprimer</Button>
                          </div>
                        </CardBody>
                      </Card>
                  );
                })}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {photoViewer.open && photoViewer.photos.length > 0 && (
        <Lightbox
          mainSrc={resolveDocumentUrl(photoViewer.photos[photoViewer.index])}
          nextSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + 1) % photoViewer.photos.length])}
          prevSrc={resolveDocumentUrl(photoViewer.photos[(photoViewer.index + photoViewer.photos.length - 1) % photoViewer.photos.length])}
          imageTitle={photoViewer.title}
          imageCaption={photoViewer.photos[photoViewer.index].filenameUser || photoViewer.photos[photoViewer.index].filename}
          onCloseRequest={() => setPhotoViewer((prev) => ({ ...prev, open: false }))}
          onMovePrevRequest={() => setPhotoViewer((prev) => ({
            ...prev,
            index: (prev.index + prev.photos.length - 1) % prev.photos.length,
          }))}
          onMoveNextRequest={() => setPhotoViewer((prev) => ({
            ...prev,
            index: (prev.index + 1) % prev.photos.length,
          }))}
          enableZoom={true}
        />
      )}
    </div>
  );
}

export default VhuLotsAdminPage;
